<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">

    <div class="row no-gutter h-100">
      <div class="col-3" v-if="showList && hasChartData">
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th width="60%">{{orderValueTableData.nameTitle}}</th>
              <th width="40%">{{orderValueTableData.valueTitle}}</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="(dataValue, index) of orderValueTableData.data" :key="index">
              <td>{{dataValue.name}}</td>
              <td><span v-html="dataValue.displayValue" /></td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
      <div :class="showList ? 'col-9' : 'col-12'">
        <ChartDoughnut
          :series="orderValueSeries"
          :legend="orderValueLegend"
          :options="orderValueOptions"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'
import AnalyticsDateMixin from '@/modules/intelligence/mixins/analyticsDate'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import ChartDoughnut from '@/components/charts/chartDoughnut'

import { ChartDateRangeTypes } from '@/modules/intelligence/types'

const OrderValueChartTypes = {
  orderValueGlass: 'orderValueGlass',
  orderValueGlassService: 'orderValueGlassService',
  orderValueOther: 'orderValueOther',
  orderValuePriceGroup: 'orderValuePriceGroup',
  orderValueStockType: 'orderValueStockType',
  orderUnitsGlass: 'orderUnitsGlass',
  orderValueContact: 'orderValueContact',
  orderValueUserValue: 'orderValueUserValue',
  orderValueUserCount: 'orderValueUserCount',
  orderValueUserDuration: 'orderValueUserDuration',
}

export default {
  name: 'OrderValueChartWidget',
  mixins: [AnalyticsDateMixin],
  components: {
    AnalyticsWidget,
    ChartDoughnut,
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    showList: Boolean,
    dateRangeType: {
      type: String,
      default: ChartDateRangeTypes.today
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    startDate: String,
    endDate: String
  },
  OrderValueChartTypes: OrderValueChartTypes,
  data() {
    return {
      orderValueSeries: [],
      orderValueTableData: {},
      orderValueLegend: [],
      orderValueOptions: {}
    }
  },
  computed: {
    hasChartData() {
      if(this.orderValueSeries) {
        return this.orderValueSeries.some(series => {
          if(series.data && series.data.length > 0)
            return true
        })
      }
      return false
    },
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    dateRangeType: function() {
      this.requestChartData()
    },
    startDate: function() {
      this.requestChartData()
    },
    endDate: function() {
      this.requestChartData()
    }
  },
  methods: {
    requestChartData() {
      var summaryFilter = {
        startDate: this.calculateStartDateForDateRangeType(this.dateRangeType, this.startDate).toDate().toISOString(),
        endDate: this.calculateEndDateForDateRangeType(this.dateRangeType, this.endDate).toDate().toISOString(),
        contactId: this.contactId,
        maxGroupSize: 7
      }

      this.$apiService.analyticsService.getOrdersValueFullSummary(summaryFilter)
      .then(response => {
        this.orderValueOptions.tooltip = {
          formatter(param) {
            return `${param.seriesName} <br />${param.name} : ${Utils.formatCurrency(param.value)} (${param.percent}%)`
          }
        }

        if(this.type == OrderValueChartTypes.orderValueGlass) {
          this.orderValueSeries = [{
            name: 'Order Item Value',
            data: response.data.orderValueGlassList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueGlassList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Value',
            data: response.data.orderValueGlassList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueGlassService) {
          this.orderValueSeries = [{
            name: 'Order Item Service Value',
            data: response.data.orderValueServiceList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueServiceList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Value',
            data: response.data.orderValueServiceList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueOther) {
          this.orderValueSeries = [{
            name: 'Order Item Other Value',
            data: response.data.orderValueOtherList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueOtherList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Value',
            data: response.data.orderValueOtherList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValuePriceGroup) {
          this.orderValueSeries = [{
            name: 'Order Item Price Group',
            data: response.data.orderValuePriceGroupList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValuePriceGroupList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Price Group',
            valueTitle: 'Value',
            data: response.data.orderValuePriceGroupList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueStockType) {
          this.orderValueSeries = [{
            name: 'Order Item Stock Type',
            data: response.data.orderValueStockTypeList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueStockTypeList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'Stock Type',
            valueTitle: 'Value',
            data: response.data.orderValueStockTypeList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderUnitsGlass) {
          this.orderValueSeries = [{
            name: 'Square Metres',
            data: response.data.orderMeterageStockList.map(group => {
              return {
                name: group.name,
                value: Utils.roundNumber(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderMeterageStockList.map(group => group.name)
          }
          this.orderValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value, false, true)}m&sup2; (${param.percent}%)`
            }
          }

          this.orderValueTableData = {
            nameTitle: 'Stock',
            valueTitle: 'Area',
            data: response.data.orderMeterageStockList.map(group => {
              return {
                name: group.name,
                displayValue: `${Utils.formatInteger(group.value, false, true)}m&sup2;`
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueContact) {
          this.orderValueSeries = [{
            name: 'Order Value Contact',
            data: response.data.orderValueContactList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueContactList.map(group => group.name)
          }
          this.orderValueTableData = {
            nameTitle: 'Contact',
            valueTitle: 'Value',
            data: response.data.orderValueContactList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueUserValue) {
          this.orderValueSeries = [{
            name: 'User Order Value',
            data: response.data.orderValueUserList.map(group => {
              return {
                name: group.name,
                value: Utils.roundCurrency(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderValueUserList.map(group => group.name)
          }

          this.orderValueTableData = {
            nameTitle: 'User',
            valueTitle: 'Value',
            data: response.data.orderValueUserList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatCurrency(group.value)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueUserCount) {
          this.orderValueSeries = [{
            name: 'User Order Count',
            data: response.data.orderCountUserList.map(group => {
              return {
                name: group.name,
                value: Utils.roundNumber(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderCountUserList.map(group => group.name)
          }
          this.orderValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value, false, true)} mins (${param.percent}%)`
            }
          }

          this.orderValueTableData = {
            nameTitle: 'User',
            valueTitle: 'Count',
            data: response.data.orderCountUserList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatInteger(group.value, false, true)
              }
            })
          }
        }

        if(this.type == OrderValueChartTypes.orderValueUserDuration) {
          this.orderValueSeries = [{
            name: 'User Order Entry Duration',
            data: response.data.orderEntryDurationUserList.map(group => {
              return {
                name: group.name,
                value: Utils.roundNumber(group.value)
              }
            }),
            label: {
              show: true,
              formatter(param) {
                return param.name
              }
            },
          }]
          this.orderValueLegend = {
            data: response.data.orderEntryDurationUserList.map(group => group.name)
          }
          this.orderValueOptions.tooltip = {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatInteger(param.value, false, true)} orders (${param.percent}%)`
            }
          }

          this.orderValueTableData = {
            nameTitle: 'User',
            valueTitle: 'Count',
            data: response.data.orderEntryDurationUserList.map(group => {
              return {
                name: group.name,
                displayValue: Utils.formatInteger(group.value, false, true) + ' mins'
              }
            })
          }
        }

        this.orderValueLegend.show = this.showLegend
      })
    },
  }
}
</script>